import { defineMessages } from 'react-intl';

const messages = defineMessages({
  carouselMaxHeight: {
    id: 'Altura (px)',
    defaultMessage: 'Altura (px)',
  },
  carouselObjectFit: {
    id: 'Ajuste da mídia (object-fit)',
    defaultMessage: 'Ajuste da mídia (object-fit)',
  },
  carouselObjectPosition: {
    id: 'Posição (object-position)',
    defaultMessage: 'Posição (object-position)',
  },
  carouselAutoPlay: {
    id: 'Auto-avanço',
    defaultMessage: 'Auto-avanço',
  },
  carouselAutoPlayInterval: {
    id: 'Intervalo do auto-avanço (ms)',
    defaultMessage: 'Intervalo do auto-avanço (ms)',
  },
});

const objectPositionChoices = [
  ['left top', 'Topo esquerdo'],
  ['center top', 'Topo centro'],
  ['right top', 'Topo direito'],
  ['left center', 'Centro esquerdo'],
  ['center center', 'Centro'],
  ['right center', 'Centro direito'],
  ['left bottom', 'Fundo esquerdo'],
  ['center bottom', 'Fundo centro'],
  ['right bottom', 'Fundo direito'],
];

export const carouselSchemaEnhancer = ({ schema, formData, intl }) => {
  if (formData?.variation !== 'carousel') return schema;

  const properties = {
    ...schema.properties,
    carouselMaxHeight: {
      title: intl.formatMessage(messages.carouselMaxHeight),
      type: 'number',
      default: 520,
      minimum: 200,
      maximum: 1200,
    },
    carouselObjectFit: {
      title: intl.formatMessage(messages.carouselObjectFit),
      type: 'string',
      default: 'contain',
      choices: [
        ['contain', 'contain'],
        ['cover', 'cover'],
      ],
    },
    carouselObjectPosition: {
      title: intl.formatMessage(messages.carouselObjectPosition),
      type: 'string',
      default: 'center center',
      choices: objectPositionChoices,
    },
    carouselAutoPlay: {
      title: intl.formatMessage(messages.carouselAutoPlay),
      type: 'boolean',
      default: false,
    },
    carouselAutoPlayInterval: {
      title: intl.formatMessage(messages.carouselAutoPlayInterval),
      type: 'number',
      default: 6000,
      minimum: 2000,
      maximum: 60000,
    },
  };

  const fieldsets = schema.fieldsets || [];
  const hasFieldset = fieldsets.some((f) => f.id === 'carousel');

  if (!hasFieldset) {
    fieldsets.push({
      id: 'carousel',
      title: 'Carrossel',
      fields: [
        'carouselMaxHeight',
        'carouselObjectFit',
        'carouselObjectPosition',
        'carouselAutoPlay',
        'carouselAutoPlayInterval',
      ],
    });
  } else {
    const idx = fieldsets.findIndex((f) => f.id === 'carousel');
    const existing = fieldsets[idx]?.fields || [];
    fieldsets[idx] = {
      ...fieldsets[idx],
      fields: Array.from(
        new Set([
          ...existing,
          'carouselMaxHeight',
          'carouselObjectFit',
          'carouselObjectPosition',
          'carouselAutoPlay',
          'carouselAutoPlayInterval',
        ]),
      ),
    };
  }

  return {
    ...schema,
    properties,
    fieldsets,
  };
};

export const mediaCarouselSchemaEnhancer = ({ schema, formData, intl }) => {
  if (formData?.variation !== 'mediaCarousel') return schema;

  const properties = {
    ...schema.properties,
    carouselMaxHeight: {
      title: intl.formatMessage(messages.carouselMaxHeight),
      type: 'number',
      default: 520,
      minimum: 200,
      maximum: 1200,
    },
    carouselObjectFit: {
      title: intl.formatMessage(messages.carouselObjectFit),
      type: 'string',
      default: 'contain',
      choices: [
        ['contain', 'contain'],
        ['cover', 'cover'],
      ],
    },
    carouselObjectPosition: {
      title: intl.formatMessage(messages.carouselObjectPosition),
      type: 'string',
      default: 'center center',
      choices: objectPositionChoices,
    },
    carouselAutoPlay: {
      title: intl.formatMessage(messages.carouselAutoPlay),
      type: 'boolean',
      default: false,
    },
    carouselAutoPlayInterval: {
      title: intl.formatMessage(messages.carouselAutoPlayInterval),
      type: 'number',
      default: 6000,
      minimum: 2000,
      maximum: 60000,
    },
  };

  const fieldsets = schema.fieldsets || [];
  const hasFieldset = fieldsets.some((f) => f.id === 'carousel');

  if (!hasFieldset) {
    fieldsets.push({
      id: 'carousel',
      title: 'Carrossel',
      fields: [
        'carouselMaxHeight',
        'carouselObjectFit',
        'carouselObjectPosition',
        'carouselAutoPlay',
        'carouselAutoPlayInterval',
      ],
    });
  } else {
    const idx = fieldsets.findIndex((f) => f.id === 'carousel');
    const existing = fieldsets[idx]?.fields || [];
    fieldsets[idx] = {
      ...fieldsets[idx],
      fields: Array.from(
        new Set([
          ...existing,
          'carouselMaxHeight',
          'carouselObjectFit',
          'carouselObjectPosition',
          'carouselAutoPlay',
          'carouselAutoPlayInterval',
        ]),
      ),
    };
  }

  return {
    ...schema,
    properties,
    fieldsets,
  };
};

export const teaserSchemaEnhancer = ({ schema, formData, intl }) => {
  if (formData?.variation !== 'teaser') return schema;

  const properties = {
    ...schema.properties,
    align: {
      widget: 'align',
      title: 'Alinhamento',
      actions: ['left', 'right', 'center'],
      default: 'left',
    },
  };

  // Se o schema já tiver fieldset styles, adiciona lá, senão no default
  const hasStyles = schema.fieldsets?.some((f) => f.id === 'styling');

  if (!hasStyles) {
    const defaultFieldset = schema.fieldsets.find((f) => f.id === 'default');
    if (defaultFieldset && !defaultFieldset.fields.includes('align')) {
      defaultFieldset.fields.push('align');
    }
  }

  // We ensure styles schema has it if it's nested (Volto 16+ uses addStyling)
  if (schema.properties?.styles?.schema) {
    schema.properties.styles.schema.properties.align = {
      widget: 'align',
      title: 'Alinhamento',
      actions: ['left', 'right', 'center'],
      default: 'left',
    };
    const stylesDefault = schema.properties.styles.schema.fieldsets.find(
      (f) => f.id === 'default',
    );
    if (stylesDefault && !stylesDefault.fields.includes('align')) {
      stylesDefault.fields.push('align');
    }
  }

  return {
    ...schema,
    properties,
  };
};
